import {
  EXCEL_GRID_SELECT_BLUR_CANCEL_DELAY_MS,
  EXCEL_GRID_SELECT_EDITOR_STYLES,
} from "../constants/excel-like-grid.constants";
import type { ExcelGridCellSelectEditorProps } from "../models/excel-grid-component-props.models";
import type { ExcelGridSelectOption } from "../models/excel-grid.models";
import { findExcelGridSelectOptionByValue } from "../utils/excel-grid.utils";
import { ExcelGridCellCustomSelectEditor } from "./ExcelGridCellCustomSelectEditor";
import { useEffect, useMemo, useRef } from "react";
import Select, { type SingleValue } from "react-select";

export const ExcelGridCellSelectEditor = ({
  value,
  options,
  isSearchable = true,
  allowCustomValue = false,
  placeholder = "Select value",
  selectMenuPortalTarget,
  onCancel,
  onCommitValue,
}: ExcelGridCellSelectEditorProps) => {
  const cancelTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasFinishedEditingRef = useRef(false);
  const selectedOption = useMemo(() => findExcelGridSelectOptionByValue(options, value), [options, value]);

  useEffect(
    () => () => {
      if (cancelTimeoutRef.current) {
        clearTimeout(cancelTimeoutRef.current);
      }
    },
    [],
  );

  const cancelOnce = () => {
    if (hasFinishedEditingRef.current) {
      return;
    }

    hasFinishedEditingRef.current = true;
    onCancel();
  };

  const commitSelectedOptionOnce = (option: SingleValue<ExcelGridSelectOption>) => {
    if (!option || hasFinishedEditingRef.current) {
      return;
    }

    if (cancelTimeoutRef.current) {
      clearTimeout(cancelTimeoutRef.current);
    }

    hasFinishedEditingRef.current = true;
    onCommitValue(option.value, true);
  };

  const scheduleCancel = () => {
    if (cancelTimeoutRef.current) {
      clearTimeout(cancelTimeoutRef.current);
    }

    cancelTimeoutRef.current = setTimeout(cancelOnce, EXCEL_GRID_SELECT_BLUR_CANCEL_DELAY_MS);
  };

  if (allowCustomValue) {
    return (
      <ExcelGridCellCustomSelectEditor
        value={value}
        options={options}
        isSearchable={isSearchable}
        placeholder={placeholder}
        selectMenuPortalTarget={selectMenuPortalTarget}
        onCancel={onCancel}
        onCommitValue={onCommitValue}
      />
    );
  }

  return (
    <Select<ExcelGridSelectOption, false>
      autoFocus
      className="excel-grid-cell-select-editor text-xs leading-4"
      classNamePrefix="excel-grid-cell-select"
      aria-label="Select selected cell value"
      data-test="excel-grid-cell-select-editor"
      value={selectedOption}
      options={options}
      isSearchable={isSearchable}
      isClearable={false}
      menuIsOpen
      menuPlacement="auto"
      maxMenuHeight={220}
      menuPortalTarget={selectMenuPortalTarget ?? (typeof document === "undefined" ? undefined : document.body)}
      placeholder={placeholder}
      noOptionsMessage={() => "No matching values"}
      styles={EXCEL_GRID_SELECT_EDITOR_STYLES}
      onChange={commitSelectedOptionOnce}
      onBlur={scheduleCancel}
      onKeyDown={(event) => {
        if (event.key !== "Escape") {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        cancelOnce();
      }}
    />
  );
};
